import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import axios, { setCurrentUserTimezone } from '@/lib/axios'
import { UserType } from '@/types/user'
import { getErrorMessage } from '@/lib/utils'

type MeStateType = {
  isLoading: boolean
  response: UserType | null
  error: {
    status?: number
    message: string
  } | null
}

type MeStoreType = {
  meState: MeStateType
  getMe: () => Promise<UserType>
  editProfile: (profileData: any) => Promise<UserType>
  reset: () => void
}

const initialState: MeStateType = {
  isLoading: false,
  response: null,
  error: null
}

const useMeStore = create<MeStoreType>()(
  devtools((set, get) => ({
    meState: initialState,
    getMe: async () => {
      // If already loading, don't make another request
      const currentState = get().meState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        meState: {
          ...initialState,
          isLoading: true,
          // Preserve existing user during refetch so session is not briefly dropped
          response: currentState.response
        }
      }))

      try {
        const { data } = await axios.get('/account/me')

        // Set a timezone for future API calls
        if (data.timezone) {
          setCurrentUserTimezone(data.timezone)
        }

        set(() => ({
          meState: {
            ...initialState,
            response: data
          }
        }))
        return data
      } catch (error: any) {
        const status = error.response?.status
        // Only clear the session for definitive auth failures (401).
        // For transient errors (5xx, network) keep the existing user so the
        // session is not dropped.
        if (status === 401) {
          set(() => ({
            meState: {
              ...initialState,
              error: {
                status,
                message: getErrorMessage(error)
              }
            }
          }))
        } else {
          set(() => ({
            meState: {
              ...currentState,
              isLoading: false,
              error: {
                status: status ?? 500,
                message: getErrorMessage(error)
              }
            }
          }))
        }
        throw error
      }
    },
    editProfile: async (profileData: any) => {
      const currentState = get().meState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        meState: {
          ...currentState,
          isLoading: true,
          error: null
        }
      }))

      try {
        const { data } = await axios.patch('/account/update', profileData, {
          headers: {
            'Content-Type': 'application/json'
          }
        })

        set(() => ({
          meState: {
            ...currentState,
            isLoading: false,
            response: data,
            error: null
          }
        }))

        // Update the timezone if it changed
        if (data.timezone) {
          setCurrentUserTimezone(data.timezone)
        }

        return data
      } catch (error: any) {
        set(() => ({
          meState: {
            ...currentState,
            isLoading: false
            // Don't clear response or set session error — edit errors are handled
            // by the calling component; they must not affect the session state.
          }
        }))
        throw error
      }
    },
    reset: () => {
      setCurrentUserTimezone(null)
      set(() => ({
        meState: initialState
      }))
    }
  }))
)

export default useMeStore
